/**
 * BbSectionSaveContext — per-letter (A-J) explicit-save registration for the
 * Brand Bible v2 Section Viewer.
 *
 * Mirrors the settings DirtyFormGuard registry (frontend/src/components/
 * settings/DirtyFormGuard.tsx) — same `{isDirty, save, discard}` shape, same
 * "shared module-level default so nothing has to wrap the page in a
 * Provider" trick — but keyed by BbLetter instead of holding a single
 * implicit slot. The Section Viewer keeps the whole page (all ten sections'
 * owning component instances) mounted across a `?section=` switch, so the
 * registry has to know WHICH letter is asking, not just "the one mounted
 * form" (settings unmounts the previous section on route change; Brand
 * Bible does not).
 *
 * Only sections in CONVERTED_SECTIONS (./convertedSections) ever call
 * useRegisterBbSectionSave — while that set is empty, nothing registers, so
 * isDirty/isAnyDirty are always false and every consumer of this context
 * (the beforeunload guard, the footer Save button, the standing Unsaved
 * indicator) is a no-op.
 *
 * Save-UX revision slate (bb-save-ux-slate-brief-20260810): the registration
 * contract grew one additive, optional field — `changeCount`. A registrant
 * that omits it is treated as a single undifferentiated change while dirty
 * (see registry.changeCount below); F/G/H/I all supply a real one computed
 * from their own buffer diff.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { BB_LETTERS, type BbLetter } from '@/lib/bbSectionMap';
import type { DirtyFormSaveResult } from '@/components/settings/DirtyFormGuard';

export type { DirtyFormSaveResult };

export interface BbSectionSaveRegistration {
  isDirty: () => boolean;
  save: () => DirtyFormSaveResult | Promise<DirtyFormSaveResult>;
  discard: () => void;
  /** Number of buffered edits, shown in the footer "Save {letter} · {n}
   *  change{s}" label and the standing Unsaved indicator. Optional —
   *  omitting it just means "some undifferentiated change" (1) while dirty. */
  changeCount?: () => number;
}

/** Outcome of a Save-all sweep — sections that failed stay dirty; every
 *  other dirty section at the time of the call was saved. */
export interface SaveAllResult {
  failed: BbLetter[];
}

interface BbSectionSaveRegistry {
  register: (letter: BbLetter, registration: BbSectionSaveRegistration) => () => void;
  isRegistered: (letter: BbLetter) => boolean;
  isDirty: (letter: BbLetter) => boolean;
  isAnyDirty: () => boolean;
  /** Canonical A-J order, not registration order. */
  dirtyLetters: () => BbLetter[];
  /** 0 when clean or unregistered; the registrant's own changeCount() while dirty, else 1. */
  changeCount: (letter: BbLetter) => number;
  save: (letter: BbLetter) => Promise<DirtyFormSaveResult>;
  /** Sequential over every currently-dirty section. A failing section keeps
   *  its own dirty state and the sweep continues — it does not abort the rest. */
  saveAll: () => Promise<SaveAllResult>;
  discard: (letter: BbLetter) => void;
  subscribe: (listener: () => void) => () => void;
  /** Fires once per "the last dirty section just cleared via a save" event —
   *  never for a discard-driven zero-crossing. Count = sections saved in that
   *  sweep. See BbCompletionMoment. */
  subscribeCompletion: (listener: (count: number) => void) => () => void;
}

function createBbSectionSaveRegistry(): BbSectionSaveRegistry {
  const forms = new Map<BbLetter, BbSectionSaveRegistration>();
  const listeners = new Set<() => void>();
  const completionListeners = new Set<(count: number) => void>();
  // Cached so useSyncExternalStore subscribers (useDirtyLetters) get a
  // referentially stable array between notify() calls — a fresh array on
  // every call, even with identical contents, risks React's tearing check
  // treating each render as "still changing" and looping.
  let dirtyLettersCache: BbLetter[] | null = null;
  // Accumulates successful saves since the registry was last fully clean;
  // consumed (and reported) the moment isAnyDirtyNow() next reads false as a
  // *result of a save*. Reset to 0 by a discard that itself reaches zero, so
  // a later unrelated save can't inherit a stale count from an abandoned batch.
  let savedSinceClean = 0;

  const notify = () => {
    dirtyLettersCache = null;
    listeners.forEach((listener) => listener());
  };

  const isAnyDirtyNow = () => Array.from(forms.values()).some((form) => form.isDirty());

  const recordSaveOutcome = (succeeded: boolean) => {
    if (succeeded) savedSinceClean += 1;
    if (savedSinceClean > 0 && !isAnyDirtyNow()) {
      const count = savedSinceClean;
      savedSinceClean = 0;
      completionListeners.forEach((listener) => listener(count));
    }
  };

  return {
    register(letter, registration) {
      forms.set(letter, registration);
      notify();
      return () => {
        if (forms.get(letter) === registration) {
          forms.delete(letter);
          notify();
        }
      };
    },
    isRegistered: (letter) => forms.has(letter),
    isDirty: (letter) => forms.get(letter)?.isDirty() ?? false,
    isAnyDirty: isAnyDirtyNow,
    dirtyLetters() {
      if (!dirtyLettersCache) {
        dirtyLettersCache = BB_LETTERS.filter((letter) => forms.get(letter)?.isDirty() ?? false);
      }
      return dirtyLettersCache;
    },
    changeCount(letter) {
      const form = forms.get(letter);
      if (!form || !form.isDirty()) return 0;
      return form.changeCount ? form.changeCount() : 1;
    },
    async save(letter) {
      const form = forms.get(letter);
      if (!form) return true;
      const result = await form.save();
      const succeeded = typeof result === 'boolean' ? result : result.success;
      recordSaveOutcome(succeeded);
      notify();
      return result;
    },
    async saveAll() {
      const failed: BbLetter[] = [];
      for (const letter of BB_LETTERS.filter((l) => forms.get(l)?.isDirty() ?? false)) {
        const form = forms.get(letter);
        if (!form) continue;
        try {
          const result = await form.save();
          const succeeded = typeof result === 'boolean' ? result : result.success;
          recordSaveOutcome(succeeded);
          if (!succeeded) failed.push(letter);
        } catch {
          recordSaveOutcome(false);
          failed.push(letter);
        }
      }
      notify();
      return { failed };
    },
    discard(letter) {
      forms.get(letter)?.discard();
      // A discard that itself brings the registry to all-clean must NOT
      // surface as a completion moment (that's a save-only celebration) —
      // drop any leftover saved-but-unreported count so it can't leak into
      // a later, unrelated save.
      if (!isAnyDirtyNow()) savedSinceClean = 0;
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    subscribeCompletion(listener) {
      completionListeners.add(listener);
      return () => completionListeners.delete(listener);
    },
  };
}

// Module-level default so BrandBiblePage doesn't have to wrap itself in a
// Provider for the common case (one page, one registry) — same trade-off
// DirtyFormGuardContext makes. BbSectionSaveProvider remains available for
// tests that want an isolated registry per render.
const defaultRegistry = createBbSectionSaveRegistry();
const BbSectionSaveContext = createContext<BbSectionSaveRegistry>(defaultRegistry);

export function BbSectionSaveProvider({ children }: { children: ReactNode }) {
  const registryRef = useRef<BbSectionSaveRegistry | null>(null);
  if (!registryRef.current) registryRef.current = createBbSectionSaveRegistry();

  return (
    <BbSectionSaveContext.Provider value={registryRef.current}>
      {children}
    </BbSectionSaveContext.Provider>
  );
}

export function useBbSectionSaveRegistry() {
  return useContext(BbSectionSaveContext);
}

/** Registers the owning section's save lifecycle; deregisters reliably on unmount
 *  or whenever `registration` changes identity (e.g. its isDirty snapshot changed). */
export function useRegisterBbSectionSave(
  letter: BbLetter,
  registration: BbSectionSaveRegistration | null,
) {
  const registry = useBbSectionSaveRegistry();

  useEffect(() => {
    if (!registration) return;
    return registry.register(letter, registration);
  }, [registry, letter, registration]);
}

/** Reactive: true while `letter` is registered AND its own isDirty() reports dirty. */
export function useIsSectionDirty(letter: BbLetter): boolean {
  const registry = useBbSectionSaveRegistry();
  return useSyncExternalStore(registry.subscribe, () => registry.isDirty(letter));
}

/** Reactive: true once any registered letter has a mounted, unsaved edit. Drives beforeunload. */
export function useIsAnySectionDirty(): boolean {
  const registry = useBbSectionSaveRegistry();
  return useSyncExternalStore(registry.subscribe, () => registry.isAnyDirty());
}

/** Reactive: true while `letter` has an active registration (drives the footer Save button). */
export function useIsSectionRegistered(letter: BbLetter): boolean {
  const registry = useBbSectionSaveRegistry();
  return useSyncExternalStore(registry.subscribe, () => registry.isRegistered(letter));
}

/** Reactive: every currently-dirty letter, canonical A-J order. Drives the
 *  standing Unsaved indicator's "● Unsaved: F · H" letter list. */
export function useDirtyLetters(): BbLetter[] {
  const registry = useBbSectionSaveRegistry();
  return useSyncExternalStore(registry.subscribe, () => registry.dirtyLetters());
}

/** Reactive: 0 when `letter` is clean/unregistered, else its buffered change
 *  count (or 1, if the registrant didn't supply one). Drives the footer
 *  "Save {letter} · {n} change{s}" label. */
export function useSectionChangeCount(letter: BbLetter): number {
  const registry = useBbSectionSaveRegistry();
  return useSyncExternalStore(registry.subscribe, () => registry.changeCount(letter));
}

/** Fires `onComplete(count)` once per save-driven "all sections now clean"
 *  transition — never for a discard-driven one. See registry.subscribeCompletion. */
export function useSectionSaveCompletion(onComplete: (count: number) => void): void {
  const registry = useBbSectionSaveRegistry();
  useEffect(() => registry.subscribeCompletion(onComplete), [registry, onComplete]);
}
